import { Button } from '@repo/ui/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/ui/dialog'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import { ScrollArea } from '@repo/ui/components/ui/scroll-area'
import { surfaceSpacing } from '@repo/ui/lib/design-tokens'
import { cn } from '@repo/ui/lib/utils'
import { MapPin, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  AppWorkspacePane,
  AppWorkspacePaneHeader,
  AppWorkspacePanel,
  AppWorkspaceSidebarDetailSplit,
} from '@/components/app/workspace-shell'
import { PlacesMap } from '@/components/operator/places-map'
import type { OperatorConfigurationState } from '@/lib/operator-config/types'
import { trpc } from '@/lib/trpc'
import {
  matchesServiceArea,
  PostalAddressField,
  type PostalAddressValue,
  parsePostalAddress,
} from './location-shared'

export function PlacesEditor({
  configuration,
  onSaved,
}: {
  configuration: OperatorConfigurationState
  onSaved?: () => Promise<void> | void
}) {
  const createPlace = trpc.locations.create.useMutation()
  const updatePlace = trpc.locations.update.useMutation()
  const deletePlace = trpc.locations.delete.useMutation()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingPlaceId, setEditingPlaceId] = useState<string | null>(null)
  const [placeLabel, setPlaceLabel] = useState('')
  const [placeAddress, setPlaceAddress] = useState<PostalAddressValue>(
    parsePostalAddress({ addressText: '' })
  )
  const viewerLocationQuery = trpc.geo.viewerLocation.useQuery()

  function resetPlaceDialog() {
    setEditingPlaceId(null)
    setPlaceLabel('')
    setPlaceAddress(parsePostalAddress({ addressText: '' }))
  }

  function openCreateDialog() {
    resetPlaceDialog()
    setDialogOpen(true)
  }

  function openEditDialog(location: OperatorConfigurationState['locations'][number]) {
    setEditingPlaceId(location.id)
    setPlaceLabel(location.label)
    setPlaceAddress(
      parsePostalAddress({
        addressText: location.addressText ?? '',
        lat: location.lat ?? undefined,
        lng: location.lng ?? undefined,
        h3Index: location.h3Index ?? undefined,
      })
    )
    setDialogOpen(true)
  }

  async function handleSavePlace() {
    const payload = {
      label: placeLabel.trim(),
      addressText: placeAddress.addressText || undefined,
      lat: placeAddress.lat,
      lng: placeAddress.lng,
      h3Index: placeAddress.h3Index,
      isServiceBase: false,
      hardRadiusMiles: null,
      softRadiusMiles: null,
      surchargeType: null,
      surchargeAmount: null,
      maxDriveTimeBetweenMinutes: null,
      isDefaultStart: false,
      isDefaultEnd: false,
    }

    if (!payload.label) {
      toast.error('Add a name for this location.')
      return
    }

    if (!payload.h3Index || !payload.lat || !payload.lng) {
      toast.error('Enter a complete address.')
      return
    }

    if (editingPlaceId) {
      await updatePlace.mutateAsync({ id: editingPlaceId, ...payload })
    } else {
      await createPlace.mutateAsync(payload)
    }

    setDialogOpen(false)
    resetPlaceDialog()
    await onSaved?.()
  }

  async function handleDeletePlace(location: OperatorConfigurationState['locations'][number]) {
    if (matchesServiceArea(location, configuration.serviceAreaPolicies)) {
      toast.error('This location is currently used by a service area. Update that area first.')
      return
    }

    await deletePlace.mutateAsync({ id: location.id })
    await onSaved?.()
  }

  const mappedLocationCount = configuration.locations.filter(
    (location) => location.lat && location.lng
  ).length

  return (
    <>
      <AppWorkspacePanel>
        <AppWorkspaceSidebarDetailSplit>
          <AppWorkspacePane as="aside" surface="region" className="flex flex-col">
            <AppWorkspacePaneHeader
              eyebrow="Saved locations"
              description={`${configuration.locations.length} saved · ${mappedLocationCount} mapped`}
              actions={
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 shrink-0 cursor-pointer gap-2"
                  onClick={openCreateDialog}
                >
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              }
            />

            <ScrollArea className="min-h-0 flex-1">
              {configuration.locations.length === 0 ? (
                <div className={cn(surfaceSpacing.inset.comfortable, 'space-y-3')}>
                  <div className="app-empty-surface p-4 text-sm text-muted-foreground">
                    No locations saved yet.
                  </div>
                  <Button className="w-full cursor-pointer gap-2" onClick={openCreateDialog}>
                    <Plus className="h-4 w-4" />
                    Add first location
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-border/70">
                  {configuration.locations.map((location) => (
                    <div
                      key={location.id}
                      className={cn(
                        'grid grid-cols-[minmax(0,1fr)_auto] items-start transition-colors hover:bg-muted/20',
                        surfaceSpacing.gap.lg,
                        surfaceSpacing.insetX.comfortable,
                        'py-3'
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 shrink-0 text-primary" />
                          <div className="truncate text-[15px] font-semibold text-foreground">
                            {location.label}
                          </div>
                        </div>
                        <div className="mt-1.5 line-clamp-2 text-sm leading-6 text-muted-foreground">
                          {location.addressText || 'Address pending'}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1 pt-0.5">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="cursor-pointer rounded-[4px]"
                          onClick={() => openEditDialog(location)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          <span className="sr-only">Edit {location.label}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="cursor-pointer rounded-[4px]"
                          onClick={() => void handleDeletePlace(location)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          <span className="sr-only">Delete {location.label}</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </AppWorkspacePane>

          <AppWorkspacePane
            surface="muted"
            minHeight
            className="grid grid-rows-[auto_minmax(0,1fr)]"
          >
            <AppWorkspacePaneHeader
              eyebrow="Map"
              description="Real saved coordinates only."
              actions={
                <div className="rounded-[4px] border border-border/70 bg-background/70 px-2.5 py-1 text-xs text-muted-foreground">
                  {mappedLocationCount} pins
                </div>
              }
            />
            <PlacesMap
              places={configuration.locations}
              className="h-full min-h-0 rounded-none border-0"
              defaultCenter={
                viewerLocationQuery.data
                  ? [viewerLocationQuery.data.lat, viewerLocationQuery.data.lng]
                  : undefined
              }
              viewportSignal={configuration.locations.length}
            />
          </AppWorkspacePane>
        </AppWorkspaceSidebarDetailSplit>
      </AppWorkspacePanel>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingPlaceId ? 'Edit location' : 'Add location'}</DialogTitle>
            <DialogDescription>
              Save named locations so routes and booking areas can reference them consistently.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={placeLabel}
                onChange={(event) => setPlaceLabel(event.target.value)}
                placeholder="Home, Studio, Office"
              />
            </div>
            <PostalAddressField value={placeAddress} onChange={setPlaceAddress} />
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleSavePlace()}
                disabled={
                  createPlace.isPending ||
                  updatePlace.isPending ||
                  !placeLabel.trim() ||
                  !placeAddress.h3Index
                }
              >
                {createPlace.isPending || updatePlace.isPending ? 'Saving…' : 'Save location'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
